/**
 * Self-correcting smooth scroll for CTA / hero anchors.
 *
 * Problem it solves: a one-shot `scrollIntoView({behavior:'smooth'})` locks onto
 * the target's position at click time. When lazy-loaded images or reveal-on-scroll
 * animated elements between the trigger and the target expand DURING the animation,
 * the target shifts down and the scroll lands short of the buy box.
 *
 * This helper recomputes the live target position on every animation frame, so it
 * keeps following the target as the layout settles, and only finishes once the
 * target has stopped moving.
 */
(() => {
  if (window.froyaScrollToSection) return;

  const REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const resolveTarget = (id) => {
    if (!id) return null;

    const direct = document.getElementById(id);
    if (direct) return direct;

    try {
      return (
        document.querySelector(`[id$="__${CSS.escape(id)}"]`) ||
        document.querySelector(`[id$="${CSS.escape(id)}"]`)
      );
    } catch (e) {
      return null;
    }
  };

  const getOffset = (opts, target) => {
    if (opts && typeof opts.offset === 'number') return opts.offset;

    // Account for a sticky/fixed header overlapping the top of the viewport.
    // The visible nav is the froya-nav section, whose STICKY element is its
    // .froya-nav-section wrapper (html.ig-nav-b is hardcoded, so it is always
    // sticky at top:0/z-index:100). The legacy store-header is display:none
    // (zero rect → skipped) but stays listed for markets/themes still on it.
    const candidates = document.querySelectorAll(
      'header[role="banner"], .section-header, .lp-custom-header, [data-sticky-header], .shopify-section--header, .froya-nav-section'
    );

    let offset = 0;

    for (const el of candidates) {
      const style = window.getComputedStyle(el);
      const position = style.position;
      if (position !== 'fixed' && position !== 'sticky') continue;

      const rect = el.getBoundingClientRect();
      if (rect.top <= 1 && rect.bottom > offset) offset = rect.bottom;
    }

    // Honor the target's own CSS scroll-margin-top as a minimum, so the JS
    // landing never sits higher than what native anchor scrolling would do
    // and anchors can declare their required clearance in one place.
    if (target) {
      const scrollMargin = parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
      if (scrollMargin > offset) offset = scrollMargin;
    }

    return offset;
  };

  /**
   * Force any lazy media between the current viewport and the target to load
   * eagerly so the layout settles faster (fewer mid-scroll shifts).
   */
  const preloadLazyMediaBefore = (target) => {
    const targetBottom = window.pageYOffset + target.getBoundingClientRect().bottom;
    const cutoff = targetBottom + window.innerHeight;
    const media = document.querySelectorAll('img[loading="lazy"], iframe[loading="lazy"]');

    for (const el of media) {
      const top = window.pageYOffset + el.getBoundingClientRect().top;
      if (top > cutoff) continue;

      el.loading = 'eager';
      if (el.dataset && el.dataset.src && !el.getAttribute('src')) {
        el.setAttribute('src', el.dataset.src);
      }
    }
  };

  const clampToDocument = (y) => {
    const maxScroll = Math.max(
      0,
      document.documentElement.scrollHeight - window.innerHeight
    );
    return Math.max(0, Math.min(y, maxScroll));
  };

  const froyaScrollToSection = (target, opts) => {
    opts = opts || {};
    if (typeof target === 'string') target = resolveTarget(target);
    if (!target) return;

    const offset = getOffset(opts, target);

    preloadLazyMediaBefore(target);

    if (REDUCE_MOTION) {
      window.scrollTo(0, clampToDocument(window.pageYOffset + target.getBoundingClientRect().top - offset));
      return;
    }

    let cancelled = false;

    const cleanup = () => {
      window.removeEventListener('wheel', onUserScroll, { passive: true });
      window.removeEventListener('touchstart', onUserScroll, { passive: true });
      window.removeEventListener('keydown', onKey);
    };

    const onUserScroll = () => {
      cancelled = true;
      cleanup();
    };

    const onKey = (e) => {
      const keys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar'];
      if (keys.includes(e.key)) onUserScroll();
    };

    window.addEventListener('wheel', onUserScroll, { passive: true });
    window.addEventListener('touchstart', onUserScroll, { passive: true });
    window.addEventListener('keydown', onKey);

    // Time-based eased tween. Duration scales with distance (px/sec speed cap)
    // so long scrolls don't whoosh, with a sensible min/max. The target Y is
    // recomputed every frame so the animation keeps following lazy-loaded /
    // animated content that shifts the buy box during the scroll.
    const startY = window.pageYOffset;
    const initialTarget = clampToDocument(
      startY + target.getBoundingClientRect().top - offset
    );
    const speed = typeof opts.speed === 'number' ? opts.speed : 900; // px per second
    const minDuration = typeof opts.duration === 'number' ? opts.duration : 700;
    const maxDuration = typeof opts.maxDuration === 'number' ? opts.maxDuration : 1000;
    const distancePx = Math.abs(initialTarget - startY);
    const duration = Math.max(minDuration, Math.min(maxDuration, (distancePx / speed) * 1000));

    // easeInOutCubic — slow start, slow end, gentle through the middle.
    const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    const start = performance.now();

    const frame = (now) => {
      if (cancelled) return;

      const targetY = clampToDocument(
        window.pageYOffset + target.getBoundingClientRect().top - offset
      );

      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = easeInOut(t);

      window.scrollTo(0, startY + (targetY - startY) * eased);

      if (t >= 1) {
        // Tween finished; if late-loading content nudged the target, run a few
        // settle frames to land precisely on the live position.
        if (Math.abs(targetY - window.pageYOffset) > 1.5 && elapsed < 4000) {
          window.scrollTo(0, window.pageYOffset + (targetY - window.pageYOffset) * 0.2);
          requestAnimationFrame(frame);
          return;
        }
        window.scrollTo(0, targetY);
        cleanup();
        return;
      }

      requestAnimationFrame(frame);
    };

    requestAnimationFrame(frame);
  };

  window.froyaScrollToSection = froyaScrollToSection;

  /**
   * Single capturing click handler. Running in the capture phase lets us
   * preventDefault + stopPropagation BEFORE the legacy inline `onclick`
   * (target phase) or the hero container's bubbling listener fire, so the
   * old one-shot scrollIntoView never runs and we don't double-scroll.
   */
  document.addEventListener(
    'click',
    (e) => {
      let id = null;

      const scrollLink = e.target.closest('[data-scroll-to]');
      if (scrollLink && scrollLink.dataset.scrollTo) {
        id = scrollLink.dataset.scrollTo;
      } else {
        const heroContainer = e.target.closest('[data-hero-link]');
        if (heroContainer && !e.target.closest('a, button')) {
          const link = heroContainer.getAttribute('data-hero-link');
          if (link && link.charAt(0) === '#') id = link.slice(1);
        }
      }

      if (!id) return;

      const target = resolveTarget(id);
      if (!target) return; // fall back to default anchor behavior

      e.preventDefault();
      e.stopPropagation();
      froyaScrollToSection(target);
    },
    true
  );
})();
